import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Card } from "react-bootstrap";
import {
  addoptions,
  editoptions,

} from "../../store/slices/package";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

export default function CopyOptionStep({
  packageId,
  options,
  setOptions,
  nextStep,
  prevStep,
}) {
  const dispatch = useDispatch();

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!options || options.length === 0) {
      setOptions([{ name: "", markupValue: "", gstPercentage: "" }]);
    }
  }, []);
  // Add new empty form row
  const handleAddRow = () => {
    setOptions((prev) => [
      ...prev,
      { name: "", markupValue: "", gstPercentage: "" },
    ]);
  };

  // Remove row
  const handleRemoveRow = (index) => {
    // Don't allow deleting the first row
    if (index === 0) return;

    setOptions((prev) => prev.filter((_, i) => i !== index));

    // Optional: remove corresponding validation errors
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle change per row
  const handleChange = (index, field, value) => {
    const updated = [...options];
    const updatedErrors = [...errors];

    if (!updatedErrors[index]) {
      updatedErrors[index] = {};
    }

    // 🔥 clone the object first
    updated[index] = { ...updated[index], [field]: value };

    if (field === "name") {
      const nameRegex = /^[a-zA-Z0-9][\s\S]*$/;

      if (!value) {
        updatedErrors[index].name = "Please enter option name";
      } else if (!nameRegex.test(value)) {
        updatedErrors[index].name = "Name cannot contain special characters";
      } else {
        updatedErrors[index].name = "";
      }
    }

    if (field === "markupValue") {
      // allows up to 5 digits before decimal and optional 2 decimal places
      const numberRegex = /^\d{1,5}(\.\d{1,2})?$/;

      if (!value) {
        updatedErrors[index].markupValue = "Please enter margin";
      } else if (!numberRegex.test(value)) {
        updatedErrors[index].markupValue =
          "Margin must be a valid number up to 5 digits";
      } else if (parseFloat(value) < 0 || parseFloat(value) > 99999) {
        updatedErrors[index].markupValue =
          "Margin must be between 0 and 99999";
      } else {
        updatedErrors[index].markupValue = "";
      }
    }

    if (field === "gstPercentage") {
      const numberRegex = /^\d+$/;
      const numValue = parseFloat(value);

      if (!value) {
        updatedErrors[index].gstPercentage = "Please enter GST";
      } else if (!numberRegex.test(value)) {
        updatedErrors[index].gstPercentage = "GST must be a valid number";
      } else if (numValue < 0 || numValue > 20) {
        updatedErrors[index].gstPercentage = "GST must be between 0 and 20";
      } else {
        updatedErrors[index].gstPercentage = "";
      }
    }

    setOptions(updated);
    setErrors(updatedErrors);
  };
  const validateOptions = () => {
    let isValid = true;
    const newErrors = [];

    options.forEach((opt, index) => {
      const rowErrors = {};

      if (!opt.name) {
        rowErrors.name = "Please enter option name";
        isValid = false;
      }

      if (opt.markupValue === "" || opt.markupValue === null) {
        rowErrors.markupValue = "Please enter margin";
        isValid = false;
      } else if (opt.markupValue < 0 || opt.markupValue > 100000) {
        rowErrors.markupValue = "Margin must be between 0 and 99999";
        isValid = false;
      }

      if (opt.gstPercentage === "" || opt.gstPercentage === null) {
        rowErrors.gstPercentage = "Please enter GST";
        isValid = false;
      } else if (opt.gstPercentage < 0 || opt.gstPercentage > 100) {
        rowErrors.gstPercentage = "GST must be between 0 and 100";
        isValid = false;
      }

      newErrors[index] = rowErrors;
    });

    setErrors(newErrors);
    return isValid;
  };

  // On Next → Save All Options
  const handleNext = async () => {
    if (!packageId) {
      toast.error("Package ID missing");
      return;
    }

    if (!validateOptions()) return;

    try {
      for (const option of options) {
        const payload = {
          packageId,
          name: option.name,
          markupValue: Number(option.markupValue),
          gstPercentage: Number(option.gstPercentage),
        };

        if (
          option._id
        ) {
          await dispatch(
            editoptions({
              id: option._id,
              ...payload,
            }),
          ).unwrap();
        } else {
          const res = await dispatch(addoptions(payload)).unwrap();

          option._id = res?._id || res?.data?._id || res?.option?._id || null;
        }
      }

      toast.success("Options saved successfully");
      nextStep();
    } catch (error) {
      toast.error("Failed to save options");
    }
  };

  return (
    <Container>
      <Card className="p-4 shadow-sm border-0 mt-2">
        <div className="d-flex justify-content-end mb-3">
          <Button
            style={{ backgroundColor: "#1E0959", border: "none" }}
            onClick={handleAddRow}
          >
            + Add Option
          </Button>
        </div>
        {options.map((opt, index) => (
          <Card key={index} className="p-3 mb-3 border">
            <Row className="g-3">
              <Col md={3}>
                <Form.Label>Option Name</Form.Label>
                <Form.Control
                  value={opt.name}
                  placeholder="e.g. Deluxe"
                  onChange={(e) => handleChange(index, "name", e.target.value)}
                  minLength={3}
                  maxLength={30}
                />
                {errors[index]?.name && (
                  <small className="text-danger">{errors[index]?.name}</small>
                )}
              </Col>

              <Col md={3}>
                <Form.Label>Margin Amount(Per Person)</Form.Label>
                <Form.Control
                  type="text"
                  value={opt.markupValue}
                  placeholder="Enter margin amount"
                  onChange={(e) =>
                    handleChange(index, "markupValue", e.target.value)
                  }
                  maxLength={5}
                  minLength={2}
                />
                {errors[index]?.markupValue && (
                  <small className="text-danger">
                    {errors[index]?.markupValue}
                  </small>
                )}
              </Col>

              <Col md={3}>
                <Form.Label>GST (%)</Form.Label>
                <Form.Control
                  type="text"
                  value={opt.gstPercentage}
                  placeholder="Enter gst percentage"
                  onChange={(e) =>
                    handleChange(index, "gstPercentage", e.target.value)
                  }
                  maxLength={2}
                  minLength={2}
                />
                {errors[index]?.gstPercentage && (
                  <small className="text-danger">
                    {errors[index]?.gstPercentage}
                  </small>
                )}
              </Col>
              {index !== 0 && (
                <Col md={2}>
                  <Form.Label>
                    &nbsp;
                    <br />
                    &nbsp;
                  </Form.Label>
                  <Button
                    variant="danger"
                    size="sm"
                    className="w-50 mt-3"
                    onClick={() => handleRemoveRow(index)}
                  >
                    Delete
                  </Button>
                </Col>
              )}
            </Row>
          </Card>
        ))}

        <div className="d-flex justify-content-between mt-4">
          <button className="cancel-btn" onClick={prevStep}>
            Previous
          </button>

          <button className="submit-btn" onClick={handleNext}>
            Next
          </button>
        </div>
      </Card>
    </Container>
  );
}
